import React from "react";
import { Link } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import {
  FREE_AI_INSIGHTS_TOTAL,
  FREE_HISTORY_VISIBLE,
  FREE_MAX_WORDS_PER_ENTRY,
  MEMBER_COACHING_SESSION_FEATURE,
  MEMBER_TRIAL_DAYS,
  SUBSCRIPTION_PRICE
} from "../constants/pricing.js";
import { ACTIONS_ROUTE, BEHAVIORAL_PATTERNS_ROUTE, DEEP_REFLECTION_ROUTE } from "../constants/routes.js";
import { EMAIL_REQUIRED_POLICY } from "../constants/emailRequiredPolicy.js";

const Faq = () => {
  return (
    <PageContainer className="about-page">
      <h1 className="about-page__title">Frequently Asked Questions</h1>

      <p className="about-page__lead">
        Answers about Quick Clarity, Deep Reflection, privacy, pricing, and who Ahavibes is for.
      </p>

      <section className="about-section">
        <h2 className="about-section__heading">What is Ahavibes?</h2>
        <p className="about-section__p">Ahavibes is a structured reasoning tool for high-functioning professionals.</p>
        <p className="about-section__p">
          It helps you separate what happened from the interpretation you added — so your next response comes from
          examined evidence, not an automatic assumption.
        </p>
        <p className="about-section__p">
          It is not a journaling app for venting. It is not a mood tracker. It is a thinking system.
        </p>
        <p className="about-section__p">
          <strong>
            Start with Quick Clarity on the home page: write what is occupying your attention, examine the
            interpretation, and leave with one testable insight. For a full Event → Interpretation → Evidence → Response
            record, use Deep Reflection.
          </strong>
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What is Quick Clarity?</h2>
        <p className="about-section__p">
          Quick Clarity is the short path on the home page. You record a thought, situation, conversation, or decision —
          then examine facts versus interpretation and leave with one primary insight you can confirm or correct.
        </p>
        <p className="about-section__p">
          You can also confirm or correct the named emotion and thinking pattern, and optionally log one next action.
          Confirmed signals feed your{" "}
          <Link to={BEHAVIORAL_PATTERNS_ROUTE}>Cognitive Behavioral Patterns</Link> views over time.
        </p>
        <p className="about-section__p">
          Optional Dictate uses your browser&apos;s speech recognition to insert text into the Quick Clarity entry box.
          Ahavibes does not store audio; see the <Link to="/disclaimer">Disclaimer</Link> for details.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What is Deep Reflection?</h2>
        <p className="about-section__p">
          Deep Reflection is the full structured path under Tools. It walks you through{" "}
          <strong>EIER</strong> — Event → Interpretation → Evidence → Response — when you want a complete thought
          record instead of a short Quick Clarity pass.
        </p>
        <p className="about-section__p">
          Open it from the menu at <Link to={DEEP_REFLECTION_ROUTE}>Deep Reflection</Link>.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Who is Ahavibes for?</h2>
        <ul className="about-section__list">
          <li>Professionals who replay conversations long after they end</li>
          <li>High-functioning adults who feel stuck in the same interpretation loops, not broken</li>
          <li>People who process more than they express — who know what they think but say it too late</li>
          <li>Thinkers who journal but want their writing to lead to a clearer response</li>
          <li>Anyone who wants to react less and reason more</li>
        </ul>
        <p className="about-section__p">
          You must be 18 or older. You do not need to be spiritual. You do not need therapy. You do not need to be in
          crisis. You only need willingness to examine your thinking.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Who is Ahavibes NOT for?</h2>
        <p className="about-section__p">
          Ahavibes is a reasoning tool, not emotional comfort software. It&apos;s not the right fit if you&apos;re looking
          for emotional validation, motivational encouragement, spiritual guidance, a therapy replacement, or crisis
          support. Users must be 18+.
        </p>
        <p className="about-section__p">We&apos;d rather tell you this now than disappoint you later.</p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">How does Ahavibes work? (The EIER Framework)</h2>
        <p className="about-section__p">
          Deep Reflection runs every session through <strong>EIER</strong> — four steps, same order, every time:
        </p>
        <p className="about-section__p">
          <strong>E — Event.</strong> What actually happened? Observable facts only. No meaning yet.
          <br />
          <strong>I — Interpretation.</strong> What story did your mind create about it?
          <br />
          <strong>E — Evidence.</strong> What facts support that story? What facts are missing?
          <br />
          <strong>R — Response.</strong> Knowing that — how do you want to respond now?
        </p>
        <p className="about-section__p">
          Quick Clarity uses the same distinction — fact versus interpretation, evidence gaps, and a clearer next
          response — in a shorter format with one primary insight.
        </p>
        <p className="about-section__p">
          The AI does not diagnose you. It mirrors your reasoning, surfaces your assumptions, and shows you where
          interpretation ran ahead of evidence.
        </p>
        <p className="about-section__p">The goal is not emotional soothing. The goal is cognitive clarity.</p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Is this based on real science?</h2>
        <p className="about-section__p">Yes — and we&apos;re transparent about the lineage.</p>
        <p className="about-section__p">
          EIER builds on the evidence-testing structure that cognitive behavioral therapy has validated over decades:
          separate the situation from the automatic thought, check the thought against evidence, choose a considered
          response. Ahavibes systematizes that structure into a daily practice.
        </p>
        <p className="about-section__p">
          The mechanism has research behind it too: putting experiences into precise words — what researchers call affect
          labeling — is a well-studied way to reduce automatic reactivity. Naming the interpretation is not a soft
          exercise. It is how the loop gets interrupted.
        </p>
        <p className="about-section__p">
          Ahavibes is not therapy and makes no clinical claims. It applies well-studied thinking structures, honestly.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What are thinking patterns?</h2>
        <p className="about-section__p">This is what makes Ahavibes different from every reflection tool you&apos;ve tried.</p>
        <p className="about-section__p">
          One reflection gives you clarity on one situation. Fifty reflections reveal something more valuable:{" "}
          <strong>the interpretation patterns you repeat.</strong> Filling in other people&apos;s motives. Replaying one
          situation without resolving it. Predicting the worst without evidence. Leaving the right words unsaid.
        </p>
        <p className="about-section__p">
          Ahavibes detects these patterns across your reflection history — named in plain language and counted, so you can
          see what repeats. On Quick Clarity, confirming (or correcting) the thinking pattern and emotion strengthens
          those signals. Patterns come from recurrence across your entries, not from a single reflection, and they are
          never permanent labels: when a pattern stops appearing, the counts show that too.
        </p>
        <p className="about-section__p">That&apos;s where one-off clarity becomes pattern intelligence.</p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What are Actions?</h2>
        <p className="about-section__p">
          After a Quick Clarity insight, you can log one optional next step. Track it as done, not yet, or changed —
          without putting your reflection text in reminder previews.
        </p>
        <p className="about-section__p">
          Open your list anytime at <Link to={ACTIONS_ROUTE}>Actions</Link>.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Why should I use Ahavibes instead of ChatGPT?</h2>
        <p className="about-section__p">ChatGPT can help you reflect once. Ahavibes shows you what repeats.</p>
        <p className="about-section__p">
          A general chatbot gives you a fresh conversation every time — no consistent framework, no memory of how you
          think, no accumulation. Ahavibes runs reflections through the same structure and builds something a chat log
          never can: <strong>a longitudinal record of your interpretations and how they held up against
          evidence.</strong> That record is where the patterns live — and the patterns are where response choices
          actually change.
        </p>
        <p className="about-section__p">One is a conversation. The other is a practice with compounding returns.</p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">How is Ahavibes different from journaling apps?</h2>
        <p className="about-section__p">
          Journaling apps give you a blank page. Blank pages produce venting, and venting produces relief that lasts about
          an hour.
        </p>
        <p className="about-section__p">
          Ahavibes gives you a structure. Structure produces examined thinking, and examined thinking produces different
          responses — in the meeting, in the message, in the moment. Then it does what no notebook can: tracks which
          thought patterns repeat across weeks, so you&apos;re not just recording your thinking. You&apos;re revising it.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">How does Ahavibes help me stop overthinking?</h2>
        <p className="about-section__p">
          Overthinking is your mind replaying a situation without new information — the same loop, no exit.
        </p>
        <p className="about-section__p">
          Ahavibes gives the loop an exit: break the situation into what happened, what you assumed, and what the evidence
          actually shows. Most loops survive on one untested assumption. When you test it, the loop loses its fuel — and
          you can choose a response instead of another replay.
        </p>
        <p className="about-section__p">
          Used a few times a week, this becomes reflexive: a pause between event and reaction — response discipline, not
          another open loop.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What is structured journaling?</h2>
        <p className="about-section__p">
          Structured journaling is guided writing that follows a repeatable framework instead of a blank page — in
          Ahavibes&apos; case, Event → Interpretation → Evidence → Response. Free-form journaling organizes your day.
          Structured journaling organizes your <em>thinking</em>: it separates facts from assumptions, tests the
          assumptions, and ends with a chosen response instead of an open loop.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">How do I sign in?</h2>
        <p className="about-section__p">
          Ahavibes uses passwordless sign-in. Enter your email, receive a one-time verification code, and enter the
          code. There is no password to remember.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Why is email required?</h2>
        {EMAIL_REQUIRED_POLICY.paragraphs.map((p) => (
          <p key={p.slice(0, 48)} className="about-section__p">
            {p}
          </p>
        ))}
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What emails will I get?</h2>
        <ul className="about-section__list">
          <li>
            <strong>Welcome</strong> — shortly after you create an account
          </li>
          <li>
            <strong>Day 7 and day 14</strong> — nudges to return if you have not recorded an insight recently; skipped
            when you are already writing consistently
          </li>
          <li>
            <strong>Day-after insight</strong> — after you examine a thought, a follow-up the next day with what you
            wrote and the primary insight, so you can write another
          </li>
          <li>
            <strong>Action reminders</strong> — only if you opt in to be reminded about an open next step (the email
            does not include your reflection text)
          </li>
        </ul>
        <p className="about-section__p">
          Product emails include an unsubscribe link. Login verification codes are account security and are not part
          of that opt-out. You can also delete your account from{" "}
          <Link to="/profile">Profile</Link> when signed in.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What can I use for free?</h2>
        <ul className="about-section__list">
          <li>
            Sign in with a verifiable email to examine thoughts with AI (up to {FREE_AI_INSIGHTS_TOTAL} AI insights on
            the free plan)
          </li>
          <li>
            Quick Clarity on the home page, plus Deep Reflection when you want a full EIER record
          </li>
          <li>
            Up to {FREE_MAX_WORDS_PER_ENTRY} words per Quick Clarity entry
          </li>
          <li>
            Last {FREE_HISTORY_VISIBLE} Quick Clarity sessions visible in Past Reflections (plus legacy reflections);
            export as JSON anytime
          </li>
          <li>
            The{" "}
            <Link to={BEHAVIORAL_PATTERNS_ROUTE}>Cognitive Behavioral Patterns</Link> hub with emotion breakdowns and
            pattern counts — full pattern details unlock with Membership
          </li>
        </ul>
        <p className="about-section__p">
          Free is for building the thinking habit and seeing your first patterns start to form. See full details on the{" "}
          <Link to="/pricing">pricing page</Link>.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">What do I get as a Member?</h2>
        <ul className="about-section__list">
          <li>Unlimited AI insights and reflection sessions</li>
          <li>
            The full patterns dashboard: your named thinking patterns, loops, and mental load trends over time — built
            from your full reflection history
          </li>
          <li>Full reflection history, nothing trimmed</li>
          <li>{MEMBER_COACHING_SESSION_FEATURE}</li>
          <li>Export anytime; your data stays yours</li>
        </ul>
        <p className="about-section__p">
          <strong>
            {MEMBER_TRIAL_DAYS}-day free trial, then {SUBSCRIPTION_PRICE}.
          </strong>{" "}
          The paid layer is pattern intelligence — not emotional reassurance. Compare plans on the{" "}
          <Link to="/pricing">pricing page</Link>.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Is my writing private?</h2>
        <p className="about-section__p">
          Yes. Your reflections are treated as personal data, and we can be specific about what that means:
        </p>
        <ul className="about-section__list">
          <li>
            Your reflections are <strong>never used to train AI models</strong>
          </li>
          <li>Your data is not shared, sold, or reused for machine learning</li>
          <li>Reflections are processed only to generate your private insight</li>
          <li>
            Optional Dictate on Quick Clarity uses browser speech recognition; Ahavibes receives the resulting text the
            same way as typed text and does not store audio
          </li>
        </ul>
        <p className="about-section__p">
          <strong>Encryption and storage.</strong> Data in transit is protected with HTTPS (TLS). Signed-in reflections
          live in secure database infrastructure. Draft text on your device before you examine it stays in the browser
          until you sign in and submit.
        </p>
        <p className="about-section__p">
          <strong>Export and deletion.</strong> Download all your reflections as JSON anytime, on any plan, from{" "}
          <Link to="/history">Past Reflections</Link>. Delete individual reflections permanently from History, or delete
          your account from <Link to="/profile">Profile</Link> when signed in. Your reflections remain yours — on every
          plan, always.
        </p>
        <p className="about-section__p">
          See the <Link to="/disclaimer">Disclaimer</Link> for full details.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Is Ahavibes therapy?</h2>
        <p className="about-section__p">No — and we take that boundary seriously.</p>
        <p className="about-section__p">
          Ahavibes is not therapy, crisis support, or a mental health service. It does not diagnose or treat conditions.
          It provides structured reflection for self-aware adults who want to think more clearly.
        </p>
        <p className="about-section__p">
          If you are experiencing severe distress, trauma, or crisis, please seek support from a licensed professional or
          local crisis services. Ahavibes is built to complement professional care, never to replace or delay it.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Who built Ahavibes?</h2>
        <p className="about-section__p">
          Ahavibes was built by Priti Solanki — backend engineer turned founder of PurpleSpot.ink, an AI strategy and
          leadership decision-coaching practice.
        </p>
        <p className="about-section__p">
          It began as the method she used with coaching clients and on her own hardest thinking days: a repeatable way to
          separate events from interpretations before responding. Ahavibes is that method, productized — with the
          measurement honesty of someone who believes an AI should never claim more about your mind than the evidence
          supports.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section__heading">Why does Ahavibes matter?</h2>
        <p className="about-section__p">
          Most adults function well externally — and internally carry unresolved interpretation loops. Replay. Assumed
          intent. Imagined conversations. The noise isn&apos;t caused by events. It&apos;s caused by unexamined stories
          about events.
        </p>
        <p className="about-section__p">
          Ahavibes provides structure where there was looping, clarity where there was assumption, and deliberation where
          there was reaction.
        </p>
        <p className="about-section__p">It turns reflection into reasoning.</p>
      </section>
    </PageContainer>
  );
};

export default Faq;
